import React from 'react';
import Prism from 'prismjs';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';
import '../../../styles/prism-custom.css';
import { getEloTier } from '../../../utils/eloUtils';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';

// Define Header styled component
const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const Title = styled(Typography)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.palette.info.main};
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const MetaInfo = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Description = styled('div')`
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
  margin-bottom: 16px;
  max-width: 100%;
`;

const DescriptionText = styled(Typography)`
  margin-bottom: 16px;
`;

const CodeContainer = styled('div')`
  max-width: 100%;
  overflow-x: auto;
  margin-bottom: 16px;

  pre {
    margin: 0;
    padding: 16px;
  }

  code {
    font-family: 'Fira Code', monospace;
    font-size: 14px;
    background-color: transparent;
  }
`;

interface ChallengeDisplayProps {
  title: string;
  description: string;
  language: string;
  eloBase: number;
  children?: React.ReactNode;
}

const ChallengeDisplay: React.FC<ChallengeDisplayProps> = ({
  title,
  description,
  language,
  eloBase,
  children
}) => {
  const indentCode = (code: string): string => {
    const lines = code.split('\n');
    let indent = 0;
    const indentSize = 4;
    
    return lines.map(line => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.match(/^[}\])]/) && indent > 0) {
        indent--;
      }
      
      const indentedLine = ' '.repeat(indent * indentSize) + trimmedLine;
      
      if (trimmedLine.match(/[{[(]$/)) {
        indent++;
      }
      
      return indentedLine;
    }).join('\n');
  };

  const highlightedDescription = React.useMemo(() => {
    if (!description) return '';
    
    const formattedCode = indentCode(description);
    return Prism.highlight(
      formattedCode,
      Prism.languages[language] || Prism.languages.javascript,
      language
    );
  }, [description, language]);

  const tier = React.useMemo(() => getEloTier(eloBase), [eloBase]);

  return (
    <div>
      <Header>
        <Title variant="h2">
          {title}
        </Title>
        <MetaInfo>
          <Typography component="span" variant="body2">
            {tier.name}
          </Typography>
          <Typography component="span" variant="body2">
            ELO: {eloBase}
          </Typography>
          <Typography component="span" variant="body2">
            {language}
          </Typography>
        </MetaInfo>
      </Header>

      <CodeContainer>
        <div className="card bg-base-300/90 shadow-xl border border-info/10">
          <div className="card-body p-0">
            <pre className="line-numbers">
              <code 
                className={`language-${language}`}
                dangerouslySetInnerHTML={{ __html: highlightedDescription }}
              />
            </pre>
          </div>
        </div>
        {children}
      </CodeContainer>
    </div>
  );
};

export default ChallengeDisplay;